<!--
  The one table primitive.

  V1 repeated plain <table> markup per view with `px-6 py-3` rows, so a large studio
  display showed very few records and there was no sorting, selection or keyboard model.
  This replaces all of that:

    * sticky header;
    * sortable columns, with the sort state announced;
    * arrow-key row navigation with a visible focus row;
    * a distinct selected row and a distinct on-air row, the latter marked by a left
      edge as well as a colour so it survives a monochrome screenshot;
    * a card list below the tablet breakpoint rather than a horizontally scrolling table.
-->
<script setup lang="ts" generic="T extends Record<string, unknown>">
import { computed, ref } from 'vue';
import ShioSkeleton from './ShioSkeleton.vue';

export interface Column<Row> {
  key: string;
  label: string;
  sortable?: boolean;
  align?: 'start' | 'end';
  width?: string;
  /** Hidden first when space is lost. */
  secondary?: boolean;
  value?: (row: Row) => unknown;
}

const props = withDefaults(
  defineProps<{
    columns: Column<T>[];
    rows: T[];
    rowKey: (row: T) => string;
    /** Row currently on air. Rendered with the on-air treatment. */
    onAirKey?: string | null;
    selectedKey?: string | null;
    emptyTitle?: string;
    emptyDescription?: string;
    caption?: string;
    /** Renders placeholder rows instead of the empty state while the first load runs. */
    loading?: boolean;
    loadingLabel?: string;
    /** Opt in to row selection and keyboard activation. Off, rows carry no tab stop. */
    selectable?: boolean;
  }>(),
  {
    onAirKey: null,
    selectedKey: null,
    emptyTitle: 'Nothing here yet',
    loading: false,
    loadingLabel: 'Loading',
    selectable: false,
  },
);

const emit = defineEmits<{ select: [row: T]; activate: [row: T] }>();

const sortKey = ref<string | null>(null);
const sortDir = ref<'asc' | 'desc'>('asc');
const focusIndex = ref(0);
const body = ref<HTMLTableSectionElement | null>(null);

const SKELETON_ROWS = 4;

const sorted = computed(() => {
  if (!sortKey.value) return props.rows;
  const column = props.columns.find((c) => c.key === sortKey.value);
  if (!column) return props.rows;
  const read = column.value ?? ((row: T) => row[column.key]);
  const factor = sortDir.value === 'asc' ? 1 : -1;
  return [...props.rows].sort((a, b) => {
    const av = read(a);
    const bv = read(b);
    if (av === bv) return 0;
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    return (av < bv ? -1 : 1) * factor;
  });
});

function toggleSort(column: Column<T>) {
  if (!column.sortable) return;
  if (sortKey.value === column.key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = column.key;
    sortDir.value = 'asc';
  }
}

function ariaSort(column: Column<T>): 'ascending' | 'descending' | 'none' | undefined {
  if (!column.sortable) return undefined;
  if (sortKey.value !== column.key) return 'none';
  return sortDir.value === 'asc' ? 'ascending' : 'descending';
}

function cellValue(row: T, column: Column<T>): unknown {
  return column.value ? column.value(row) : row[column.key];
}

function onKeydown(event: KeyboardEvent, index: number, row: T) {
  if (!props.selectable) return;
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    focusIndex.value = Math.min(index + 1, sorted.value.length - 1);
    focusRow(focusIndex.value);
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    focusIndex.value = Math.max(index - 1, 0);
    focusRow(focusIndex.value);
  } else if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    emit('activate', row);
  }
}

function onSelect(row: T) {
  if (props.selectable) emit('select', row);
}

function onActivate(row: T) {
  if (props.selectable) emit('activate', row);
}

/** Scoped to this table's own body, so two tables on one page do not steal focus. */
function focusRow(index: number) {
  const el = body.value?.querySelector<HTMLElement>(`[data-shio-row-index="${index}"]`);
  el?.focus();
}
</script>

<template>
  <div v-if="loading && sorted.length === 0" class="shio-table-wrap">
    <table class="shio-table" aria-busy="true">
      <caption v-if="caption" class="shio-sr-only">
        {{
          caption
        }}
      </caption>
      <thead>
        <tr>
          <th
            v-for="column in columns"
            :key="column.key"
            :style="column.width ? { width: column.width } : undefined"
          >
            {{ column.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="index in SKELETON_ROWS" :key="index">
          <td v-for="column in columns" :key="column.key">
            <ShioSkeleton :label="loadingLabel" :width="index % 2 === 0 ? '55%' : '80%'" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <div v-else-if="sorted.length === 0" class="shio-panel shio-panel__body">
    <slot name="empty">
      <div class="shio-empty">
        <p class="shio-card-title">{{ emptyTitle }}</p>
        <p v-if="emptyDescription" class="shio-body-sm">
          {{ emptyDescription }}
        </p>
      </div>
    </slot>
  </div>

  <template v-else>
    <div class="shio-table-wrap shio-table-wrap--wide">
      <table class="shio-table">
        <caption v-if="caption" class="shio-sr-only">
          {{
            caption
          }}
        </caption>
        <thead>
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :style="column.width ? { width: column.width } : undefined"
              :aria-sort="ariaSort(column)"
            >
              <button
                v-if="column.sortable"
                class="shio-table__sort"
                type="button"
                @click="toggleSort(column)"
              >
                {{ column.label }}
                <span aria-hidden="true">{{
                  sortKey === column.key ? (sortDir === 'asc' ? '▲' : '▼') : '·'
                }}</span>
              </button>
              <template v-else>{{ column.label }}</template>
            </th>
          </tr>
        </thead>
        <tbody ref="body">
          <tr
            v-for="(row, index) in sorted"
            :key="rowKey(row)"
            :data-shio-row-index="index"
            :data-onair="onAirKey === rowKey(row) ? 'true' : undefined"
            :aria-selected="selectable ? selectedKey === rowKey(row) : undefined"
            :tabindex="selectable ? 0 : undefined"
            @click="onSelect(row)"
            @dblclick="onActivate(row)"
            @keydown="onKeydown($event, index, row)"
          >
            <td
              v-for="column in columns"
              :key="column.key"
              :style="column.align === 'end' ? { textAlign: 'right' } : undefined"
            >
              <slot :name="`cell-${column.key}`" :row="row" :value="cellValue(row, column)">
                {{ cellValue(row, column) }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Below the tablet breakpoint the table becomes a card list rather than a
         horizontally scrolling grid, so nothing important is off-screen. -->
    <ul class="shio-table-cards">
      <li
        v-for="row in sorted"
        :key="rowKey(row)"
        class="shio-panel shio-table-cards__item"
        :class="{ 'shio-panel--onair': onAirKey === rowKey(row) }"
        @click="onSelect(row)"
      >
        <dl>
          <template v-for="column in columns" :key="column.key">
            <div v-if="!column.secondary" class="shio-table-cards__row">
              <dt class="shio-label">{{ column.label }}</dt>
              <dd>
                <slot :name="`cell-${column.key}`" :row="row" :value="cellValue(row, column)">
                  {{ cellValue(row, column) }}
                </slot>
              </dd>
            </div>
          </template>
        </dl>
      </li>
    </ul>
  </template>
</template>

<style scoped>
.shio-table-cards {
  display: none;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: var(--shio-space-2);
}

.shio-table-cards__item {
  padding: var(--shio-space-3);
}

.shio-table-cards__row {
  display: flex;
  justify-content: space-between;
  gap: var(--shio-space-3);
  padding: 2px 0;
}

.shio-table-cards__row dd {
  margin: 0;
  text-align: right;
}

@media (max-width: 767px) {
  .shio-table-wrap--wide {
    display: none;
  }

  .shio-table-cards {
    display: flex;
    flex-direction: column;
  }
}
</style>
